<template>
  <div class="success-page">    
    <div class="success-page__wrapper">
        <div class="success-page__logo">
            <!-- Логотип VPN как на главной странице -->
            <!-- <MainLogo class="success-page__logo-main"/> -->
            <CircleSmall class="success-page__logo-circle-small"/>
            <CircleMedium class="success-page__logo-circle-medium"/>
            <CircleBig class="success-page__logo-circle-big"/>
            
            <!-- Иконка успеха поверх логотипа -->
            <SuccessLogo class="success-page__success-logo"/>
        </div>
        <div class="success-page__buttons">
          <BaseButton @click="() => router.push('/')" styles="primary-gradient" text="Перейти на главную"/>
            <!-- Кнопка убрана, теперь используется HomeButton -->
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import confetti from 'canvas-confetti'

import BaseButton from '@/ui/components/Base/BaseButton.vue'
import HomeButton from '@/ui/components/HomeButton.vue'
import SuccessLogo from '@/assets/icons/success-icon.svg'
import CircleSmall from '@/assets/icons/thunder-small.svg'
import CircleMedium from '@/assets/icons/thunder-meduim.svg'
import CircleBig from '@/assets/icons/thunder-big.svg'

import { useUserStore } from '@/stores/useUser'
import { useSubscriptionStore } from '@/entities/Subscription/model/subscriptionStore'
import { SentryService } from '@/shared/lib/sentry/sentryService'
import { useVibration } from '@/shared/utils/system/vibration'
import { fireRealisticConfetti } from '@/shared/composables/fireRealisticConfetti'

const router = useRouter()
const userStore = useUserStore()
const subscriptionStore = useSubscriptionStore()
const { vibratePattern } = useVibration()

// Защита от прямого перехода на страницу успеха
onBeforeMount(() => {
  // Проверяем, пришел ли пользователь с оплаты
  const cameFromPayment = sessionStorage.getItem('paymentSuccess')
  
  console.log('SuccessPage: проверка флага оплаты:', cameFromPayment)
  
  if (!cameFromPayment) {
    // Для разработки - добавляем возможность тестировать конфетти
    if (import.meta.env.DEV) {
      console.log('SuccessPage: режим разработки, запускаем конфетти для тестирования')
      setTimeout(() => {
        fireRealisticConfetti()
      }, 500)
      return
    }
    
    // Логируем попытку прямого доступа к странице успеха
    SentryService.setContext('success_page_direct_access', {
      reason: 'no_payment_success_flag',
      user_agent: navigator.userAgent,
      referrer: document.referrer
    })
    
    SentryService.captureMessage('Пользователь попытался напрямую перейти на страницу успеха', 'warning')
    
    router.push('/')
    return
  }
  
  // Логируем успешное достижение страницы успеха
  SentryService.setContext('success_page_reached', {
    came_from_payment: true,
    user_tg_id: userStore.user?.tg_id || null,
    user_username: userStore.user?.username || null,
    subscription_id: subscriptionStore.subscription?.id || null
  })
  
  SentryService.captureMessage('Пользователь успешно достиг страницы подтверждения оплаты', 'info')
  
  // Очищаем флаг после использования
  sessionStorage.removeItem('paymentSuccess')
  
  // Запускаем реалистичное конфетти после небольшой задержки
  setTimeout(() => {
    console.log('SuccessPage: запускаем реалистичное конфетти после оплаты')
    fireRealisticConfetti()
  }, 500)
})

onMounted(() => {
  // Дополнительная инициализация при монтировании, если нужно
})
</script>

<style lang="scss">
.success-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow-y: hidden;
  overflow-x: hidden;

  &__wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__logo {
    position: relative;
    margin-top: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 250px;
    height: 220px;
    margin: 140px auto 0 auto;

    &-main {
      position: relative;
      z-index: 1;
    }

    &-circle-small {
      position: absolute;
      animation: pulse--circle-small 4.8s linear infinite;
      animation-delay: 0s;
    }

    &-circle-medium {
      position: absolute;
      animation: pulse--circle-medium 4.8s linear infinite;
      animation-delay: 0s;
    }

    &-circle-big {
      position: absolute;
      animation: pulse--circle-big 4.8s linear infinite;
      animation-delay: 0s;
    }
  }

  &__success-logo {
    position: absolute;
    z-index: 2;
  }

  &__back-btn {
    display: flex;

    > button {
      padding: 16px 20px;
      justify-content: center;
    }
  }

  &__buttons {
    button {
      justify-content: center;
      height: 100%;
    }
  }
}

// Анимации кругов - скопированы с HomePage
@keyframes pulse--circle-small {
    0% {
      transform: scale(1);
      opacity: 0;
    }
    45.8% { /* 2.2s из 4.8s - начало анимации маленького круга */
      transform: scale(1);
      opacity: 0;
    }
    63.5% { /* 3.05s из 4.8s - середина анимации маленького круга */
      opacity: 1;
    }
    81.3% { /* 3.9s из 4.8s - конец анимации маленького круга */
      transform: scale(1.3);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
}

@keyframes pulse--circle-medium {
    0% {
      transform: scale(1);
      opacity: 0;
    }
    63.5% { /* 3.05s из 4.8s - начало анимации среднего круга (во время пика small) */
      transform: scale(1);
      opacity: 0;
    }
    81.3% { /* 3.9s из 4.8s - середина анимации среднего круга */
      opacity: 1;
    }
    99% { /* 4.75s из 4.8s - конец анимации среднего круга */
      transform: scale(1.5);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
}

@keyframes pulse--circle-big {
    0% {
      transform: scale(1);
      opacity: 0;
    }
    81.3% { /* 3.9s из 4.8s - начало анимации большого круга (во время пика medium) */
      transform: scale(1);
      opacity: 0;
    }
    99% { /* 4.75s из 4.8s - середина анимации большого круга */
      opacity: 1;
    }
    100% { /* 4.8s - конец анимации большого круга */
      transform: scale(1.8);
      opacity: 0;
    }
}
</style>